// Copyright (c) 2022-2023. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { EmailField } from "../../../../../../../io/hyperify/frontend/components/fields/email/EmailField";
import { AuthSessionData } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import { useEmailValidator } from "../../../../../../../io/hyperify/frontend/hooks/useEmailValidator";
import { MY_NEW_CLIENT_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import {
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_ADDRESS_BACK_BUTTON_LABEL,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_ADDRESS_SUBMIT_BUTTON_LABEL,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_ADDRESS_TITLE,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_CLIENT_TYPE_BACK_BUTTON_LABEL,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_CLIENT_TYPE_SUBMIT_BUTTON_LABEL,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_CLIENT_TYPE_TITLE, T_MY_ORDER_VIEW_NEW_CLIENT_STEP_EMAIL_TITLE,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_BACK_BUTTON_LABEL,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_SUBMIT_BUTTON_LABEL,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_TITLE,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_SUBMIT_ERROR_TEXT,
    T_MY_ORDER_VIEW_NEW_CLIENT_STEP_SUBMIT_ERROR_TITLE,
    T_NEW_CLIENT_FIELD_ADDRESS_LABEL,
    T_NEW_CLIENT_FIELD_COMPANY_CODE_LABEL,
    T_NEW_CLIENT_FIELD_COMPANY_LABEL,
    T_NEW_CLIENT_FIELD_COUNTRY_LABEL, T_NEW_CLIENT_FIELD_EMAIL_LABEL,
    T_NEW_CLIENT_FIELD_FIRSTNAME_LABEL,
    T_NEW_CLIENT_FIELD_LASTNAME_LABEL,
    T_NEW_CLIENT_FIELD_MOBILE_LABEL,
    T_NEW_CLIENT_FIELD_PHONE_LABEL,
    T_NEW_CLIENT_FIELD_POST_NAME_LABEL,
    T_NEW_CLIENT_FIELD_POSTCODE_LABEL
} from "../../../../../core/constants/translation";
import { Form } from "../../../../../../../io/hyperify/frontend/components/form/Form";
import { useCallback, useState } from "react";
import { TextField } from "../../../../../../../io/hyperify/frontend/components/fields/text/TextField";
import { SubmitButton } from "../../../../../../../io/hyperify/frontend/components/submitButton/SubmitButton";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { SendanorClientHttpService } from "../../../../../core/services/SendanorClientHttpService";
import { NewClientDTO } from "../../../../../../../io/hyperify/core/store/types/client/NewClientDTO";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { ComponentUtils } from "../../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import { LanguageService } from "../../../../../../../io/hyperify/core/LanguageService";
import { ClientDTO } from "../../../../../../../io/hyperify/core/store/types/client/ClientDTO";
import { useVoidCallback } from "../../../../../../../io/hyperify/frontend/hooks/useVoidCallback";
import { VoidCallback } from "../../../../../../../io/hyperify/core/interfaces/callbacks";
import { useRealNameValidator } from "../../../../../../../io/hyperify/frontend/hooks/useRealNameValidator";
import { useAddressValidator } from "../../../../../../../io/hyperify/frontend/hooks/useAddressValidator";
import { usePhoneValidator } from "../../../../../../../io/hyperify/frontend/hooks/usePhoneValidator";
import { useCompanyValidator } from "../../../../../../../io/hyperify/frontend/hooks/useCompanyValidator";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import "./MyNewClientArticle.scss";

const LOG = LogService.createLogger('MyOrderNewClientArticle');

export interface MyNewClientArticleProps {
    readonly className   ?: string;
    readonly t            : TranslationFunction;
    readonly session      : AuthSessionData;
    readonly isCompany    : boolean;
    readonly backAction   : VoidCallback;
    readonly submitAction : (client: ClientDTO) => void;
}

export enum MyNewClientArticleStep {
    CLIENT_TYPE = 0,
    ADDRESS = 1,
    PHONE_OR_EMAIL = 2,
    SUBMITTING = 3,
    ERROR = 4
}

export function MyNewClientArticle (props: MyNewClientArticleProps) {

    const className = props?.className;
    const t = props?.t;
    const isCompany = props?.isCompany ?? false;
    const session = props?.session;
    const sessionToken = session?.token;
    const isEmailSession = !!session?.email;
    const [ step, setStep ] = useState<MyNewClientArticleStep>(MyNewClientArticleStep.CLIENT_TYPE);
    const [ company, setCompany ] = useState<string>('');
    const [ companyCode, setCompanyCode ] = useState<string>('');
    const [ companyOk ] = useCompanyValidator(company, companyCode, true);
    const [ firstName, setFirstName ] = useState<string>('');
    const [ lastName, setLastName ] = useState<string>('');
    const [ realNameOk ] = useRealNameValidator(firstName, lastName);
    const [ address, setAddress ] = useState<string>('');
    const [ postCode, setPostCode ] = useState<string>('');
    const [ postName, setPostName ] = useState<string>('');
    const [ country, setCountry ] = useState<string>('Suomi');
    const [ addressOk] = useAddressValidator(address, postCode, postName, country);
    const [ email, setEmail ] = useState<string>('');
    const [ phone, setPhone ] = useState<string>('');
    const [ mobile, setMobile ] = useState<string>('');
    const [ emailOk] = useEmailValidator(email, false);
    const [ phoneOk] = usePhoneValidator(phone, false);
    const [ mobileOk] = usePhoneValidator(mobile, false);
    const backActionProp = props?.backAction;
    const submitActionProp = props?.submitAction;

    const backAction = useVoidCallback('MyOrderNewClientArticle.backAction', backActionProp);

    const submitAction = useCallback(
        (client: ClientDTO) => {
            if (submitActionProp) {
                LOG.debug(`Submit client: `, client);
                submitActionProp(client);
            } else {
                LOG.debug(`No submit client action defined: `, client);
            }
        },
        [
            submitActionProp
        ]
    );

    const createNewClientDTOFromState = useCallback(
        (): NewClientDTO => {
            return {
                company,
                companyCode,
                firstName,
                lastName,
                address,
                postCode,
                postName,
                country,
                email,
                phone,
                mobile,
                fax: '',
                billingLang: LanguageService.getCurrentLanguage(),
                sendEmail: true,
                sendPost: false
            };
        },
        [
            company,
            companyCode,
            firstName,
            lastName,
            address,
            postCode,
            postName,
            country,
            email,
            phone,
            mobile
        ]
    );

    const onSubmit = useCallback(
        () => {
            if ( !sessionToken ) {
                throw new TypeError('Session token not defined');
            }
            setStep(MyNewClientArticleStep.SUBMITTING);
            const newClient: NewClientDTO = createNewClientDTOFromState();
            SendanorClientHttpService.addMyClient(newClient, sessionToken).then((response: ClientDTO) => {
                LOG.info('Success, added client: ', response);
                submitAction(response);
            }, (err) => {
                LOG.error(`Submitting failed: `, err);
                setStep(MyNewClientArticleStep.ERROR);
            }).catch(err => {
                LOG.error(`Exception while submitting: `, err);
            });
        },
        [
            createNewClientDTOFromState,
            sessionToken,
            submitAction
        ]
    );

    const onNextStep = useCallback(
        () => {
            setStep((state: MyNewClientArticleStep) => state + 1);
        },
        [
            setStep
        ]
    );

    const onBackButton = useCallback(
        () => {
            setStep((state: MyNewClientArticleStep) => {
                if (state === MyNewClientArticleStep.CLIENT_TYPE) {
                    backAction();
                    return state;
                } else {
                    return state - 1;
                }
            });
        },
        [
            setStep,
            backAction
        ]
    );

    return (
        <article
            className={
                MY_NEW_CLIENT_ARTICLE_CLASS_NAME
                + (className ? ` ${className}` : '')
            }
        >

            {step === MyNewClientArticleStep.CLIENT_TYPE ? (
                <Form
                    className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-form`}
                    submit={onNextStep}
                >

                    <h3>{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_CLIENT_TYPE_TITLE)}</h3>

                    {isCompany ? (
                        <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                            <TextField
                                placeholder={t(T_NEW_CLIENT_FIELD_COMPANY_LABEL)}
                                value={company}
                                change={(value: string | undefined) => setCompany(value ?? '')}
                            />
                            <TextField
                                placeholder={t(T_NEW_CLIENT_FIELD_COMPANY_CODE_LABEL)}
                                value={companyCode}
                                change={(value: string | undefined) => setCompanyCode(value ?? '')}
                            />
                        </div>
                    ) : null}

                    <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                        <TextField
                            placeholder={t(T_NEW_CLIENT_FIELD_FIRSTNAME_LABEL)}
                            value={firstName}
                            change={(value: string | undefined) => setFirstName(value ?? '')}
                        />
                        <TextField
                            placeholder={t(T_NEW_CLIENT_FIELD_LASTNAME_LABEL)}
                            value={lastName}
                            change={(value: string | undefined) => setLastName(value ?? '')}
                        />
                    </div>

                    <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                        <Button
                            style={ButtonStyle.SECONDARY}
                            className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button back-button button`}
                            click={onBackButton}
                        >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_CLIENT_TYPE_BACK_BUTTON_LABEL)}</Button>
                        <SubmitButton
                            className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button button`}
                            enabled={ isCompany ? realNameOk && companyOk : realNameOk }
                        >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_CLIENT_TYPE_SUBMIT_BUTTON_LABEL)}</SubmitButton>
                    </div>

                </Form>
            ) : (
                step === MyNewClientArticleStep.ADDRESS ? (
                    <Form
                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-form`}
                        submit={onNextStep}
                    >
                        <h3>{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_ADDRESS_TITLE)}</h3>
                        <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                            <TextField
                                placeholder={t(T_NEW_CLIENT_FIELD_ADDRESS_LABEL)}
                                value={address}
                                change={(value: string | undefined) => setAddress(value ?? '')}
                            />
                        </div>
                        <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                            <TextField
                                placeholder={t(T_NEW_CLIENT_FIELD_POSTCODE_LABEL)}
                                value={postCode}
                                change={(value: string | undefined) => setPostCode(value ?? '')}
                            />
                            <TextField
                                placeholder={t(T_NEW_CLIENT_FIELD_POST_NAME_LABEL)}
                                value={postName}
                                change={(value: string | undefined) => setPostName(value ?? '')}
                            />
                        </div>
                        <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                            <TextField
                                placeholder={t(T_NEW_CLIENT_FIELD_COUNTRY_LABEL)}
                                value={country}
                                change={(value: string | undefined) => setCountry(value ?? '')}
                            />
                        </div>
                        <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                            <Button
                                style={ButtonStyle.SECONDARY}
                                className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button back-button button`}
                                click={onBackButton}
                            >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_ADDRESS_BACK_BUTTON_LABEL)}</Button>
                            <SubmitButton
                                className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button button`}
                                enabled={addressOk}
                            >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_ADDRESS_SUBMIT_BUTTON_LABEL)}</SubmitButton>
                        </div>
                    </Form>
                ) : (
                    step === MyNewClientArticleStep.PHONE_OR_EMAIL ? (
                        isEmailSession ? (
                            <Form
                                className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-form`}
                                submit={onSubmit}
                            >
                                <h3>{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_TITLE)}</h3>
                                <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                                    <TextField
                                        placeholder={t(T_NEW_CLIENT_FIELD_PHONE_LABEL)}
                                        value={phone}
                                        change={(value: string | undefined) => setPhone(value ?? '')}
                                    />
                                    <TextField
                                        placeholder={t(T_NEW_CLIENT_FIELD_MOBILE_LABEL)}
                                        value={mobile}
                                        change={(value: string | undefined) => setMobile(value ?? '')}
                                    />
                                </div>
                                <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                                    <Button
                                        style={ButtonStyle.SECONDARY}
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button back-button button`}
                                        click={onBackButton}
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_BACK_BUTTON_LABEL)}</Button>
                                    <SubmitButton
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button buy-button button`}
                                        enabled={ phoneOk && mobileOk }
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_SUBMIT_BUTTON_LABEL)}</SubmitButton>
                                </div>
                            </Form>
                        ) : (
                            <Form
                                className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-form`}
                                submit={onSubmit}
                            >
                                <h3>{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_EMAIL_TITLE)}</h3>
                                <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                                    <EmailField
                                        placeholder={t(T_NEW_CLIENT_FIELD_EMAIL_LABEL)}
                                        value={email}
                                        change={(value: string | undefined) => setEmail(value ?? '')}
                                    />
                                </div>
                                <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                                    <Button
                                        style={ButtonStyle.SECONDARY}
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button back-button button`}
                                        click={onBackButton}
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_BACK_BUTTON_LABEL)}</Button>
                                    <SubmitButton
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button buy-button button`}
                                        enabled={ emailOk }
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_SUBMIT_BUTTON_LABEL)}</SubmitButton>
                                </div>
                            </Form>
                        )
                    ) : (
                        step === MyNewClientArticleStep.SUBMITTING ? (
                            <Form
                                className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-form`}
                                submit={onSubmit}
                            >
                                <h3>{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_TITLE)}</h3>
                                <Loader />
                                <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                                    <Button
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button back-button button`}
                                        click={() => setStep(MyNewClientArticleStep.PHONE_OR_EMAIL)}
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_BACK_BUTTON_LABEL)}</Button>
                                </div>
                            </Form>
                        ) : (
                            <Form
                                className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-form`}
                                submit={onSubmit}
                            >
                                <h3>{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_SUBMIT_ERROR_TITLE)}</h3>
                                <p>{ComponentUtils.splitWithLineBreaks(t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_SUBMIT_ERROR_TEXT))}</p>
                                <div className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                                    <Button
                                        style={ButtonStyle.SECONDARY}
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button back-button button`}
                                        click={() => setStep(MyNewClientArticleStep.PHONE_OR_EMAIL)}
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_BACK_BUTTON_LABEL)}</Button>
                                    <SubmitButton
                                        className={`${MY_NEW_CLIENT_ARTICLE_CLASS_NAME}-button buy-button button`}
                                    >{t(T_MY_ORDER_VIEW_NEW_CLIENT_STEP_PHONE_SUBMIT_BUTTON_LABEL)}</SubmitButton>
                                </div>
                            </Form>
                        )
                    )
                )
            )}

        </article>
    );

}
